import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AdminUser } from '../models/resource/admin-user.model';
import { AuthResponse } from '../models/auth-response.model'
import { LoginPayload } from '../models/resource/login-playload.model'

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly TOKEN_KEY = 'jonke_admin_token';
  private readonly USER_KEY  = 'jonke_admin_user';

  currentUser = signal<AdminUser | null>(this.getStoredUser());

  constructor(private http: HttpClient, private router: Router) {}

  login(payload: LoginPayload): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/admin/auth/login`, payload).pipe(
      tap(res => {
        localStorage.setItem(this.TOKEN_KEY, res.data.token);
        localStorage.setItem(this.USER_KEY, JSON.stringify(res.data.admin));
        this.currentUser.set(res.data.admin);
      })
    );
  }

  resetPassword(email: string): Observable<void> {
    return this.http.post<void>(`${environment.apiUrl}/admin/auth/forgot-password`, { email });
  }

  resetPasswordWithToken(token: string, email: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/admin/auth/reset-password`, {
      token, email, password: newPassword, password_confirmation: newPassword
    });
  }

  /**
   * Public patient password reset (from the email reset link). Posts to the
   * patient auth broker, not the admin one. No admin session is involved.
   */
  resetPatientPasswordWithToken(token: string, email: string, newPassword: string): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/auth/reset-password`, {
      token, email, password: newPassword, password_confirmation: newPassword
    });
  }

  logout(): void {
    this.http.post(`${environment.apiUrl}/admin/auth/logout`, {}).subscribe();
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.currentUser.set(null);
    this.router.navigate(['/auth/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  hasRole(...roles: string[]): boolean {
    const user = this.currentUser();
    return !!user && roles.includes(user.role);
  }

  isAdmin():        boolean { return this.hasRole('admin'); }
  isStaff():        boolean { return this.hasRole('admin', 'staff'); }

  updateCurrentUser(user: AdminUser): void {
    this.currentUser.set(user);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  private getStoredUser(): AdminUser | null {
    const u = localStorage.getItem(this.USER_KEY);
    return u ? JSON.parse(u) : null;
  }
}
